/**
 * pointInPolygon.ts — a tiny, dependency-free ray-casting hit test (T-512).
 *
 * The vicinity readout (T-504) already ships each block / block-group polygon to the client, but
 * nothing tells us WHICH one contains the searched site. This answers that with the site point and
 * the served geometry — no new endpoint, no PostGIS round trip, no map dependency. It is the same
 * question the map fill answers visually, made available to the stats panel.
 *
 * Coordinates are GeoJSON order — [lng, lat] in the rings, and the point is passed the same way
 * (`pointInPolygon(lng, lat, geom)`). Getting that order wrong silently returns the transpose of
 * the right answer, so it is asserted directly in the tests.
 *
 * Correctness notes:
 *   - Even-odd rule per ring: a point inside an odd number of rings of a polygon is inside the
 *     polygon, so an interior hole (a second ring) correctly excludes points that fall in it.
 *   - MultiPolygon is inside if the point is inside ANY of its polygons.
 *   - Non-areal geometries (Point, LineString, …) are never "inside" — they enclose no area.
 */
import type * as GeoJSON from 'geojson'

type Ring = GeoJSON.Position[]

/** Standard ray-casting: is (lng, lat) inside this single closed ring? */
function inRing(lng: number, lat: number, ring: Ring): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i]
    const pj = ring[j]
    if (!pi || !pj) continue
    const xi = pi[0] ?? 0
    const yi = pi[1] ?? 0
    const xj = pj[0] ?? 0
    const yj = pj[1] ?? 0
    // Does the horizontal ray at `lat` cross this edge, and is the crossing to the right of `lng`?
    const intersects = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    if (intersects) inside = !inside
  }
  return inside
}

/** Even-odd across all rings of one polygon: outer ring includes, inner rings (holes) exclude. */
function inPolygon(lng: number, lat: number, rings: Ring[]): boolean {
  let inside = false
  for (const ring of rings) {
    if (inRing(lng, lat, ring)) inside = !inside
  }
  return inside
}

/**
 * Is the point (lng, lat) inside the given GeoJSON geometry? Polygon and MultiPolygon are
 * supported; any other geometry type returns false (nothing to be inside of).
 */
export function pointInPolygon(lng: number, lat: number, geometry: GeoJSON.Geometry): boolean {
  if (geometry.type === 'Polygon') {
    return inPolygon(lng, lat, geometry.coordinates)
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.some((rings) => inPolygon(lng, lat, rings))
  }
  return false
}
